<script lang="ts">
export default {
  name: 'MobileAppPage',
  customOptions: {},
};
</script>

<script lang="ts" setup>
import { AppUpdate } from '@capawesome/capacitor-app-update';
import { isAndroid, isIOS, deviceDetect } from 'mobile-device-detect';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import GridLines from '@exchange/assets/grid-lines.svg';
import AppHeader from '@exchange/layouts/app-header/AppHeader.vue';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { capIsNativePlatform, capAppLauncher } from '@exchange/libs/utils/capacitor/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';

const router = useRouter();
const { t } = useI18n({ useScope: 'global' });
const { isXS } = useMQBreakpoints();
const isNative = capIsNativePlatform();

const iOSLink = 'https://apps.apple.com/at/app/one-trading/id1545308731';
const androidLink = 'https://play.google.com/store/apps/details?id=com.bitpanda.exchange';

type PlatformItem = {
  name: string;
  link: string;
};
const platforms: Array<PlatformItem> = [
  {
    name: 'apple',
    link: iOSLink,
  },
  {
    name: 'google',
    link: androidLink,
  },
];

const goBack = () => {
  router.go(-1);
};

const openAppStore = async () => {
  const countryToUseForIOSLookup = 'at'; // https://github.com/robingenz/capacitor-app-update/issues/12

  try {
    await AppUpdate.openAppStore({
      country: countryToUseForIOSLookup,
    });
  } catch (error) {
    logger.error('Failed to open app store', error);
  }
};
const openBrowser = (link: string) => {
  window.location.href = link;
};
const doRedirectToStore = () => {
  if (isNative) {
    logger.log('Openning native store');
    openAppStore();
    return;
  }

  if (isAndroid) {
    logger.log('Openning android link');
    openBrowser(androidLink);
  } else if (isIOS) {
    logger.log('Openning ios link');
    openBrowser(iOSLink);
  } else {
    logger.log('Detected device', deviceDetect());
  }
};

const onLogoClicked = (item: PlatformItem) => {
  if (isNative) {
    logger.log(`${item.name} clicked => openning native store`);
    capAppLauncher.openUrl({ url: item.link });
    return;
  }

  logger.log(`${item.name} clicked => openning browser`);
  openBrowser(iOSLink);
};

doRedirectToStore();
</script>

<template>
  <x-button
    v-if="isXS"
    class="mobile-app-page__header mobile-app-page__header--mobile"
    variant="clear"
    @click="goBack"
  >
    <ot-icon name="arrow-left" />
  </x-button>
  <app-header
    v-else
    class="mobile-app-page__header"
  />

  <div :class="['mobile-app-page', { 'mobile-app-page--mobile': isXS }]">
    <grid-lines class="mobile-app-page__grid-lines"></grid-lines>
    <div class="mobile-app-page__blur"></div>
    <div class="mobile-app-page__content">
      <div class="mobile-app-page__item">
        <h2>{{ t('modules.profile.getApp.title') }}</h2>
      </div>
      <div class="mobile-app-page__item">
        <div class="mobile-app-page__description">{{ t('modules.profile.getApp.description') }}</div>
      </div>
      <div class="mobile-app-page__item mobile-app-page__item--plarform">
        <x-logo
          v-for="item in platforms"
          :key="item.name"
          :name="item.name"
          class="mobile-app-page__platform-logo"
          type="badges"
          @clicked="onLogoClicked(item)"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.mobile-app-page {
  --grid-horizontal-offset: 20%;

  display: grid;
  flex: 1 1 auto;
  background-color: rgb(var(--v-theme-elevation-0));
  grid-template-columns: var(--grid-horizontal-offset) auto var(--grid-horizontal-offset);

  &__header {
    z-index: 2;

    &--mobile {
      --x-transition-slide-background-color: transparent;

      position: absolute !important; /** .xs .x-transition-slide > * breaks it */
      height: 54px;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    margin-top: 15vh;
    gap: 42px;
    grid-column-start: 2;
  }

  &__grid-lines {
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
  }

  &__blur {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 28%;
    height: 40%;
    border-radius: 317px;
    background: linear-gradient(180deg, #00a3c296 0%, #0e7bf800 86.35%);
    filter: blur(200px);
    transform: translate(-50%, -50%);
  }

  &__item {
    display: flex;
    justify-content: center;
    margin: 0 auto;

    &--plarform {
      z-index: 2;
      gap: 8px;
    }
  }

  &__description {
    color: rgb(var(--v-theme-text-secondary));
  }

  &__platform-logo {
    cursor: pointer;

    img {
      width: 200px;
    }
  }
}

.mobile-app-page--mobile {
  .mobile-app-page {
    &__blur {
      width: 57%;
      height: 50%;
    }

    &__item {
      &--plarform {
        flex-direction: column;
      }
    }
  }
}
</style>
